import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import {
  Scale,
  Mail,
  Building2,
  Calendar,
  ChevronLeft,
  ChevronRight,
  UserPlus,
  Users,
  Check,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/shared/lib/supabase";
import { useState } from "react";
import { ADMIN_ROUTES } from "@/shared/constants/paths";
import CircularProgress from "@/shared/components/CircularProgress";
import { AddLawyerDialog } from "./components/AddLawyerDialog";
import AdminLayout from "@/pages/layouts/AdminLayout";
import LawyerSearch from "@/pages/admin/components/LawyerSearch";

interface PendingLawyerApplication {
  user_id: number;
  email: string;
  full_name: string;
  first_name: string;
  last_name: string;
  firm_name: string | null;
  bar_number: string | null;
  applied_at: string;
}

const LawyersList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const itemsPerPage = 10;

  // Fetch pending lawyer applications
  const { data: pendingApplications, isLoading: pendingLoading } = useQuery({
    queryKey: ["admin-pending-lawyer-applications"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("users")
        .select("user_id, email, full_name, first_name, last_name, customizations")
        .eq("customizations->lawyer_application->>status", "pending")
        .order("created_at", { ascending: false });

      if (error) throw error;

      return (data || []).map((user) => ({
        user_id: user.user_id,
        email: user.email,
        full_name:
          user.full_name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || "Unknown",
        first_name: user.first_name,
        last_name: user.last_name,
        firm_name: user.customizations?.lawyer_application?.firm_name || null,
        bar_number: user.customizations?.lawyer_application?.bar_number || null,
        applied_at: user.customizations?.lawyer_application?.applied_at || null,
      })) as PendingLawyerApplication[];
    },
  });

  // Fetch all lawyers with user details
  const {
    data: lawyersData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["admin-lawyers", currentPage],
    queryFn: async () => {
      // Get lawyers with user info
      const query = supabase
        .from("lawyers")
        .select(
          `
          *,
          user:users(user_id, email, first_name, last_name, full_name, created_at)
        `,
          { count: "exact" },
        )
        .order("created_at", { ascending: false })
        .range((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage - 1);

      const { data, error, count } = await query;

      if (error) throw error;

      const filteredData = data || [];

      // Get assignment counts for each lawyer
      const lawyerUserIds = filteredData.map((l) => l.user_id);
      const { data: assignmentCounts } = await supabase
        .from("lawyer_estates")
        .select("lawyer_user_id")
        .in("lawyer_user_id", lawyerUserIds)
        .eq("status", "active");

      // Count assignments per lawyer
      const countMap: Record<number, number> = {};
      assignmentCounts?.forEach((a) => {
        countMap[a.lawyer_user_id] = (countMap[a.lawyer_user_id] || 0) + 1;
      });

      return {
        lawyers: filteredData.map((lawyer) => ({
          ...lawyer,
          assignmentCount: countMap[lawyer.user_id] || 0,
        })),
        totalCount: count || 0,
      };
    },
  });

  // Accept lawyer application mutation
  const acceptMutation = useMutation({
    mutationFn: async (application: PendingLawyerApplication) => {
      // Create lawyer record
      const { error: lawyerError } = await supabase.from("lawyers").insert({
        user_id: application.user_id,
        firm_name: application.firm_name,
        bar_number: application.bar_number,
      });

      if (lawyerError) throw lawyerError;

      // Update user's application status
      const { data: userData } = await supabase
        .from("users")
        .select("customizations")
        .eq("user_id", application.user_id)
        .single();

      const updatedCustomizations = {
        ...userData?.customizations,
        lawyer_application: {
          ...userData?.customizations?.lawyer_application,
          status: "approved",
          approved_at: new Date().toISOString(),
        },
      };

      const { error: updateError } = await supabase
        .from("users")
        .update({ customizations: updatedCustomizations })
        .eq("user_id", application.user_id);

      if (updateError) throw updateError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-pending-lawyer-applications"] });
      queryClient.invalidateQueries({ queryKey: ["admin-lawyers"] });
    },
  });

  // Deny lawyer application mutation
  const denyMutation = useMutation({
    mutationFn: async (userId: number) => {
      const { data: userData } = await supabase
        .from("users")
        .select("customizations")
        .eq("user_id", userId)
        .single();

      const updatedCustomizations = {
        ...userData?.customizations,
        lawyer_application: {
          ...userData?.customizations?.lawyer_application,
          status: "denied",
          denied_at: new Date().toISOString(),
        },
      };

      const { error } = await supabase
        .from("users")
        .update({ customizations: updatedCustomizations })
        .eq("user_id", userId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-pending-lawyer-applications"] });
    },
  });

  const totalPages = Math.ceil((lawyersData?.totalCount || 0) / itemsPerPage);

  const handleViewLawyer = (lawyerId: number) => {
    navigate(ADMIN_ROUTES.LAWYER_DETAIL.replace(":lawyerId", String(lawyerId)));
  };

  const handleAccept = (application: PendingLawyerApplication) => {
    acceptMutation.mutate(application);
  };

  const handleDeny = (userId: number) => {
    if (confirm("Are you sure you want to deny this application?")) {
      denyMutation.mutate(userId);
    }
  };

  return (
    <AdminLayout
      title="Lawyers"
      titleLink={ADMIN_ROUTES.LAWYERS}
      backgroundColor="stone-100"
      searchComponent={<LawyerSearch />}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl text-stone-900">Lawyers</h1>
              <p className="text-stone-600 text-sm">
                Manage lawyer accounts and estate assignments
              </p>
            </div>

            <Button onClick={() => setIsAddDialogOpen(true)}>
              <UserPlus className="h-4 w-4 mr-2" />
              Add Lawyer
            </Button>
          </div>
        </div>

        {/* Pending Applications Card */}
        {!pendingLoading && pendingApplications && pendingApplications.length > 0 && (
          <Card className="mb-6 bg-orange-50 border-orange-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2 mb-3">
                Pending
                <span className="text-[10px] bg-amber-600 text-white min-w-6 h-6 inline-flex items-center justify-center rounded-full leading-none">
                  {pendingApplications.length}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="divide-y divide-orange-100">
                {pendingApplications.map((application) => (
                  <div
                    key={application.user_id}
                    className="py-4 flex items-center justify-between first:pt-0 last:pb-0"
                  >
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-stone-900">
                        {application.full_name}
                      </h3>
                      <p className="text-sm text-stone-500 mt-0.5">{application.email}</p>
                      {(application.firm_name || application.bar_number) && (
                        <p className="text-sm text-stone-400 mt-1.5">
                          {application.firm_name}
                          {application.firm_name && application.bar_number && " · "}
                          {application.bar_number && `Bar #${application.bar_number}`}
                        </p>
                      )}
                      {application.applied_at && (
                        <p className="text-sm text-stone-400 mt-0.5">
                          Applied {new Date(application.applied_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        onClick={() => handleDeny(application.user_id)}
                        disabled={denyMutation.isPending}
                        className="text-red-600 hover:text-red-700 hover:bg-red-100"
                      >
                        <X className="h-4 w-4 mr-1" />
                        Deny
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => handleAccept(application)}
                        disabled={acceptMutation.isPending}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Accept
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Lawyers List */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">All Lawyers</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-12">
                <CircularProgress />
              </div>
            ) : error ? (
              <div className="text-center py-12 text-red-600">
                Error loading lawyers. Please try again.
              </div>
            ) : !lawyersData?.lawyers || lawyersData.lawyers.length === 0 ? (
              <div className="text-center py-12">
                <Scale className="h-12 w-12 text-stone-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-stone-900 mb-2">No lawyers found</h3>
                <p className="text-stone-600 mb-4">Add lawyers to manage estate assignments.</p>
                <Button onClick={() => setIsAddDialogOpen(true)}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add First Lawyer
                </Button>
              </div>
            ) : (
              <>
                <div className="divide-y divide-stone-100">
                  {lawyersData.lawyers.map((lawyer) => (
                    <div
                      key={lawyer.id}
                      className="py-4 flex items-center justify-between hover:bg-stone-50 -mx-6 px-6 cursor-pointer transition-colors"
                      onClick={() => handleViewLawyer(lawyer.id)}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="text-sm font-medium text-stone-900">
                            {lawyer.user?.full_name ||
                              `${lawyer.user?.first_name || ""} ${lawyer.user?.last_name || ""}`.trim() ||
                              "Unknown"}
                          </h3>
                          <Badge variant="secondary" className="gap-1">
                            <Users className="h-4 w-4" />
                            {lawyer.assignmentCount} estates
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-stone-600">
                          <span className="flex items-center gap-1">
                            <Mail className="h-4 w-4" />
                            {lawyer.user?.email}
                          </span>
                          {lawyer.firm_name && (
                            <span className="flex items-center gap-1">
                              <Building2 className="h-4 w-4" />
                              {lawyer.firm_name}
                            </span>
                          )}
                          {lawyer.bar_number && (
                            <span className="text-stone-500">Bar #{lawyer.bar_number}</span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-4 text-sm text-stone-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {new Date(lawyer.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-6 pt-6 border-t border-stone-100">
                    <p className="text-sm text-stone-600">
                      Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                      {Math.min(currentPage * itemsPerPage, lawyersData.totalCount)} of{" "}
                      {lawyersData.totalCount} lawyers
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-sm text-stone-600">
                        Page {currentPage} of {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Lawyer Dialog */}
      <AddLawyerDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        onSuccess={() => {
          refetch();
          setIsAddDialogOpen(false);
        }}
      />
    </AdminLayout>
  );
};

export default LawyersList;
